import React from 'react';
import { Box, IconButton, Link, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';

// TikTok doesn't have a built-in MUI icon, so we'll use a custom SVG
const TikTokIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="24"
    height="24"
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const socialLinks = [
  { name: 'Facebook', icon: FacebookIcon, url: 'https://www.facebook.com/aisuretech/' },
  { name: 'Instagram', icon: InstagramIcon, url: 'https://www.instagram.com/aisuretech' },
  { name: 'TikTok', icon: TikTokIcon, url: 'https://www.tiktok.com/@aisuretech' },
  { name: 'LinkedIn', icon: LinkedInIcon, url: 'https://www.linkedin.com/company/aisuretech' },
  { name: 'X', icon: XIcon, url: 'https://x.com/aisuretech' },
  { name: 'YouTube', icon: YouTubeIcon, url: 'https://www.youtube.com/@AISureTech' },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();

  const navigationLinks = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Resources', path: '/resources' },
    { label: 'Privacy', path: '/privacy' },
    { label: 'Terms', path: '/terms' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        px: 2,
        mt: 'auto',
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        mb: { xs: 7, md: 0 }, // Space for BottomNavigation on mobile only
      }}
    >
      <Stack spacing={3} alignItems="center">
        {/* Navigation Links - Desktop only */}
        <Stack
          direction="row"
          spacing={3}
          flexWrap="wrap"
          justifyContent="center"
          sx={{ display: { xs: 'none', md: 'flex' } }}
        >
          {navigationLinks.map((link) => (
            <Link
              key={link.path}
              component="button"
              variant="body2"
              onClick={() => navigate(link.path)}
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                },
              }}
            >
              {link.label}
            </Link>
          ))}
        </Stack>

        {/* Social Media */}
        <Stack spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Follow us on social media
          </Typography>
          <Stack direction="row" spacing={1}>
            {socialLinks.map((social) => {
              const IconComponent = social.icon;
              return (
                <IconButton
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <IconComponent />
                </IconButton>
              );
            })}
          </Stack>
        </Stack>

        {/* Copyright */}
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} Create A Dish. All rights reserved.
        </Typography>
      </Stack>
    </Box>
  );
};

export default Footer;
